import React, { useEffect, useState, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Shield, Lock, Database, Cookie, UserCheck, Mail } from 'lucide-react';
const sections = [{
  id: 'introduction',
  label: 'Introduction'
}, {
  id: 'data-collection',
  label: 'Data Collection'
}, {
  id: 'lawful-basis',
  label: 'Lawful Basis'
}, {
  id: 'data-sharing',
  label: 'Data Sharing & Security'
}, {
  id: 'cookies',
  label: 'Cookies & Tracking'
}, {
  id: 'your-rights',
  label: 'Your Rights'
}, {
  id: 'data-retention',
  label: 'Data Retention'
}, {
  id: 'contact',
  label: 'Contact'
}];
const policyLinks = [{
  href: '/privacy-policy',
  label: 'Privacy & Cookie Policy',
  active: true
}, {
  href: '/cancellation-policy',
  label: 'Cancellation Policy'
}, {
  href: '/terms-conditions',
  label: 'Terms & Conditions'
}];
export function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('introduction');
  useEffect(() => {
    document.title = 'Privacy & Cookie Policy | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'How we protect your data. UK GDPR compliant privacy practices for medico-legal instructions and expert witness services.');
    setMeta('robots', 'index, follow');
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px'
    });
    sections.forEach(({
      id
    }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-919">
      <Navigation data-id="element-920" />

      <div className="pt-32 pb-20 md:pt-40 md:pb-24" data-id="element-921">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-922">
          <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16" data-id="element-923">
            {/* Sticky Sidebar */}
            <aside className="hidden lg:block" data-id="element-924">
              <div className="sticky top-32" data-id="element-925">
                <nav aria-label="Legal policies navigation" className="mb-8" data-id="element-926">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-927">
                    Policies
                  </p>
                  <ul className="space-y-1" data-id="element-928">
                    {policyLinks.map(link => <li key={link.href} data-id="element-929">
                        <a href={link.href} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${link.active ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-930">
                          {link.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>

                <nav aria-label="Page sections navigation" data-id="element-931">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-932">
                    On this page
                  </p>
                  <ul className="space-y-1" data-id="element-933">
                    {sections.map(section => <li key={section.id} data-id="element-934">
                        <a href={`#${section.id}`} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${activeSection === section.id ? 'bg-warm-100 text-ink-900 font-semibold' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-935">
                          {section.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="max-w-[800px]" data-id="element-936">
              {/* Mobile policy nav */}
              <div className="lg:hidden flex gap-2 mb-8 overflow-x-auto pb-2" data-id="element-937">
                {policyLinks.map(link => <a key={link.href} href={link.href} className={`whitespace-nowrap text-sm py-2 px-4 rounded-full border transition-colors ${link.active ? 'bg-teal-50 border-teal-200 text-teal-700 font-semibold' : 'border-warm-200 text-ink-500 hover:border-teal-300'}`} data-id="element-938">
                    {link.label}
                  </a>)}
              </div>

              {/* Header */}
              <div className="mb-10" data-id="element-939">
                <div className="flex items-center gap-3 mb-4" data-id="element-940">
                  <div className="w-10 h-10 bg-warm-50 rounded-xl border border-warm-200 flex items-center justify-center" data-id="element-941">
                    <Shield className="h-5 w-5 text-slate-600" data-id="element-942" />
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#1A2B3C] tracking-tight" data-id="element-943">
                    Privacy & Cookie Policy
                  </h1>
                </div>
                <p className="text-sm text-ink-400 font-medium" data-id="element-944">
                  Last Updated: March 2026 · UK Medicolegal Assessment Ltd
                </p>
              </div>

              {/* Data Sovereignty Badge */}
              <div className="bg-[#1A2B3C] text-white rounded-2xl p-6 md:p-8 mb-12" data-id="element-945">
                <div className="flex items-start gap-4" data-id="element-946">
                  <div className="w-12 h-12 rounded-xl bg-slate-700 border border-slate-600 flex items-center justify-center flex-shrink-0" data-id="element-947">
                    <Database className="h-6 w-6 text-teal-400" data-id="element-948" />
                  </div>
                  <div data-id="element-949">
                    <p className="text-sm font-bold uppercase tracking-wider text-teal-400 mb-1" data-id="element-950">
                      UK Data Residency Guaranteed
                    </p>
                    <p className="text-slate-300 text-sm leading-relaxed" data-id="element-951">
                      All medical records and case data are processed and stored
                      exclusively on UK-based, GDPR-compliant cloud
                      infrastructure. No data leaves UK jurisdiction.
                    </p>
                  </div>
                </div>
              </div>

              {/* 1. Introduction */}
              <section id="introduction" className="mb-14" data-id="element-952">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-953">
                  1. Introduction
                </h2>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-954">
                  UK Medico-Legal Assessments ("we", "us", "our") is committed
                  to the highest standards of data protection. This policy
                  outlines how we handle personal data in compliance with the UK
                  GDPR and the Data Protection Act 2018.
                </p>
              </section>

              {/* 2. Data Collection & Usage */}
              <section id="data-collection" className="mb-14" data-id="element-955">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-956">
                  2. Data Collection & Usage
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-957">
                  We process personal data to provide expert witness and
                  medico-legal reporting services.
                </p>

                <div className="space-y-6" data-id="element-958">
                  <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6" data-id="element-959">
                    <h3 className="text-lg font-bold text-[#1A2B3C] mb-3 flex items-center" data-id="element-960">
                      <UserCheck className="h-5 w-5 mr-2.5 text-slate-500" data-id="element-961" />
                      Instructing Parties
                    </h3>
                    <p className="text-ink-600 leading-relaxed" style={{
                    lineHeight: '1.8'
                  }} data-id="element-962">
                      We collect contact details, professional registration
                      (SRA/GMC), and billing information.
                    </p>
                  </div>

                  <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6" data-id="element-963">
                    <h3 className="text-lg font-bold text-[#1A2B3C] mb-3 flex items-center" data-id="element-964">
                      <Lock className="h-5 w-5 mr-2.5 text-slate-500" data-id="element-965" />
                      Clients (Subjects)
                    </h3>
                    <p className="text-ink-600 leading-relaxed" style={{
                    lineHeight: '1.8'
                  }} data-id="element-966">
                      We process sensitive "Special Category" data, including
                      medical records, psychiatric history, and case
                      documentation, solely for the purpose of clinical
                      assessment and court-ready reporting.
                    </p>
                  </div>
                </div>
              </section>

              {/* 3. Lawful Basis for Processing */}
              <section id="lawful-basis" className="mb-14" data-id="element-967">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-968">
                  3. Lawful Basis for Processing
                </h2>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-id="element-969">
                  <table className="w-full" data-id="element-970">
                    <thead className="bg-warm-50" data-id="element-971">
                      <tr data-id="element-972">
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-973">
                          Basis
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-974">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-975">
                      <tr data-id="element-976">
                        <td className="px-6 py-4 text-ink-900 font-semibold" data-id="element-977">
                          Contractual Necessity
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-978">
                          To fulfil your instructions
                        </td>
                      </tr>
                      <tr data-id="element-979">
                        <td className="px-6 py-4 text-ink-900 font-semibold" data-id="element-980">
                          Legal Obligation
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-981">
                          To meet our duties as medical experts to the Court
                        </td>
                      </tr>
                      <tr data-id="element-982">
                        <td className="px-6 py-4 text-ink-900 font-semibold" data-id="element-983">
                          Legitimate Interests
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-984">
                          To manage our agency operations and secure clinical
                          governance
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* 4. Data Sharing & Security */}
              <section id="data-sharing" className="mb-14" data-id="element-985">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-986">
                  4. Data Sharing & Security
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-987">
                  We do not sell personal data. Information is shared only with:
                </p>

                <ul className="space-y-4 mb-8" data-id="element-988">
                  <li className="flex items-start gap-3" data-id="element-989">
                    <span className="text-teal-500 mt-1 font-bold" data-id="element-990">•</span>
                    <div data-id="element-991">
                      <strong className="text-[#1A2B3C]" data-id="element-992">
                        Assigned Experts:
                      </strong>
                      <span className="text-ink-600" data-id="element-993">
                        {' '}
                        GMC-registered consultants allocated to your case.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-994">
                    <span className="text-teal-500 mt-1 font-bold" data-id="element-995">•</span>
                    <div data-id="element-996">
                      <strong className="text-[#1A2B3C]" data-id="element-997">
                        The Courts/Tribunals:
                      </strong>
                      <span className="text-ink-600" data-id="element-998">
                        {' '}
                        As mandated by legal proceedings.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-999">
                    <span className="text-teal-500 mt-1 font-bold" data-id="element-1000">•</span>
                    <div data-id="element-1001">
                      <strong className="text-[#1A2B3C]" data-id="element-1002">
                        Secure Processors:
                      </strong>
                      <span className="text-ink-600" data-id="element-1003">
                        {' '}
                        GDPR-compliant IT and encrypted storage providers.
                      </span>
                    </div>
                  </li>
                </ul>

                <div className="bg-[#1A2B3C] text-white rounded-2xl p-6" data-id="element-1004">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1005">
                    <Lock className="h-5 w-5 text-teal-400" data-id="element-1006" />
                    <p className="font-bold" data-id="element-1007">Security</p>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed" data-id="element-1008">
                    All data is encrypted at rest and in transit using
                    industry-standard protocols. Our infrastructure is ISO 27001
                    certified and hosted exclusively within UK data centres.
                  </p>
                </div>
              </section>

              {/* 5. Cookies & Tracking */}
              <section id="cookies" className="mb-14" data-id="element-1009">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1010">
                  5. Cookies & Tracking
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1011">
                  Our website uses cookies to improve user experience:
                </p>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden mb-6" data-id="element-1012">
                  <table className="w-full" data-id="element-1013">
                    <thead className="bg-warm-50" data-id="element-1014">
                      <tr data-id="element-1015">
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-1016">
                          Type
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#1A2B3C]" data-id="element-1017">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-1018">
                      <tr data-id="element-1019">
                        <td className="px-6 py-4 text-ink-900 font-semibold" data-id="element-1020">
                          Essential Cookies
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1021">
                          Necessary for site functionality
                        </td>
                      </tr>
                      <tr data-id="element-1022">
                        <td className="px-6 py-4 text-ink-900 font-semibold" data-id="element-1023">
                          Analytical Cookies
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1024">
                          To understand site traffic (e.g., Google Analytics).
                          These are anonymised.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1025">
                  You can manage cookie preferences via your browser settings.
                  Disabling essential cookies may affect site functionality.
                </p>
              </section>

              {/* 6. Your Rights */}
              <section id="your-rights" className="mb-14" data-id="element-1026">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1027">
                  6. Your Rights
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1028">
                  Under the UK GDPR, you have the right to{' '}
                  <strong className="text-[#1A2B3C]" data-id="element-1029">access</strong>,{' '}
                  <strong className="text-[#1A2B3C]" data-id="element-1030">rectify</strong>, or{' '}
                  <strong className="text-[#1A2B3C]" data-id="element-1031">erase</strong> your
                  personal data. You may also restrict processing, request data
                  portability, or object to processing based on legitimate
                  interests.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1032">
                  To exercise these rights, contact our Data Protection Officer
                  at{' '}
                  <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1033">
                    info@uk-medicolegal-assessment.com
                  </a>
                  .
                </p>
              </section>

              {/* 7. Data Retention */}
              <section id="data-retention" className="mb-14" data-id="element-1034">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1035">
                  7. Data Retention
                </h2>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1036">
                  We retain personal data for as long as necessary to fulfil the
                  purposes outlined in this policy and to comply with legal,
                  regulatory, and professional obligations. Medical reports and
                  case files are typically retained for 7 years in line with
                  professional indemnity requirements.
                </p>
              </section>

              {/* 8. Contact & Complaints */}
              <section id="contact" className="mb-14" data-id="element-1037">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1038">
                  8. Contact & Complaints
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1039">
                  For privacy-related queries or to exercise your rights,
                  contact our Data Protection Officer:
                </p>

                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-id="element-1040">
                  <div className="flex items-center gap-3" data-id="element-1041">
                    <Mail className="h-5 w-5 text-teal-600 flex-shrink-0" data-id="element-1042" />
                    <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-bold text-lg transition-colors" data-id="element-1043">
                      info@uk-medicolegal-assessment.com
                    </a>
                  </div>
                </div>

                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1044">
                  You also have the right to lodge a complaint with the
                  Information Commissioner's Office (ICO) at{' '}
                  <a href="https://ico.org.uk" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1045">
                    ico.org.uk
                  </a>
                  .
                </p>
              </section>
            </main>
          </div>
        </div>
      </div>

      <Footer data-id="element-1046" />
    </div>;
}